// src/components/onboarding/OnboardingStepSidebar.tsx

import { Box, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useBranding } from "@/context/BrandingContext";

export type StepInfo = {
  key: string;
  label: string;
};

type Props = {
  steps: StepInfo[];
  currentStep: number;
  completedSteps: Set<string>;
  onStepClick: (index: number) => void;
};

export default function OnboardingStepSidebar({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
}: Props) {
  const { branding } = useBranding();
  return (
    <Box
      p="xl"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Logo + name */}
      <Box mb="xl">
        <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src={branding.logoNoTextUrl ?? branding.logoUrl}
            alt={branding.productName}
            style={{ width: 32, height: 32 }}
          />
          <Text fw={700} size="lg">
            {branding.productName}
          </Text>
        </Box>
        <Text size="xs" c="dimmed" mt="sm">
          Get ready to automate your analyses in just 4 steps!
        </Text>
      </Box>

      {/* Steps */}
      <Stack gap={0} style={{ flex: 1, paddingTop: 24 }}>
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(step.key);
          const isActive = index === currentStep;
          const isFuture = !isCompleted && !isActive;
          const isClickable = isCompleted;

          return (
            <Box key={step.key} style={{ position: "relative" }}>
              {/* Connecting line */}
              {index < steps.length - 1 && (
                <Box
                  style={{
                    position: "absolute",
                    left: 17,
                    top: 36,
                    width: 2,
                    height: 40,
                    backgroundColor: isCompleted
                      ? "var(--mantine-color-green-5)"
                      : "var(--mantine-color-gray-3)",
                  }}
                />
              )}

              <UnstyledButton
                onClick={() => isClickable && onStepClick(index)}
                disabled={!isClickable}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "12px 0",
                  cursor: isClickable ? "pointer" : "default",
                  opacity: isFuture ? 0.5 : 1,
                  transition: "opacity 150ms ease",
                }}
              >
                {/* Circle */}
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    backgroundColor: isCompleted
                      ? "var(--mantine-color-green-5)"
                      : isActive
                        ? "var(--mantine-color-blue-6)"
                        : "var(--mantine-color-gray-2)",
                    color: isCompleted || isActive ? "white" : "var(--mantine-color-gray-6)",
                    fontSize: 14,
                    fontWeight: 600,
                    boxShadow: isActive
                      ? "0 0 0 3px var(--mantine-color-blue-1)"
                      : isCompleted
                        ? "0 0 0 3px var(--mantine-color-green-1)"
                        : "none",
                    transition: "all 150ms ease",
                  }}
                >
                  {isCompleted ? <IconCheck size={18} /> : index + 1}
                </Box>

                {/* Label */}
                <Text
                  size="md"
                  fw={isActive ? 700 : 500}
                  c={isFuture ? "dimmed" : undefined}
                >
                  {step.label}
                </Text>
              </UnstyledButton>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
